import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { compareFractionalIndex } from '@shared';
import { generateKeyBetween } from 'fractional-indexing';
import type { AssistantFavoriteView } from '../_generated';
import { AssistantFavoritesActions } from './favorites.actions';

export const assistantFavoritesFeatureKey = 'assistantFavorites';

export interface AssistantFavoritesState extends EntityState<AssistantFavoriteView> {
  error: string | null;
}

const entityAdapter: EntityAdapter<AssistantFavoriteView> =
  createEntityAdapter<AssistantFavoriteView>({
    selectId: (view) => view.assistantId,
    sortComparer: (a, b) => compareFractionalIndex(a.sortOrder, b.sortOrder),
  });

const initialState: AssistantFavoritesState = entityAdapter.getInitialState({
  error: null,
});

export const assistantFavoritesFeature = createFeature({
  name: assistantFavoritesFeatureKey,
  reducer: createReducer(
    initialState,
    on(AssistantFavoritesActions.loadSuccess, (state, { favorites }) =>
      entityAdapter.setAll(favorites, { ...state, error: null }),
    ),
    on(AssistantFavoritesActions.loadFailure, (state, { error }) => ({
      ...state,
      error,
    })),

    on(AssistantFavoritesActions.addFavorite, (state, { assistantId }) =>
      entityAdapter.upsertOne(
        {
          assistantId,
          sortOrder: generateKeyBetween(null, getFirstSortOrder(state)),
        },
        { ...state, error: null },
      ),
    ),
    on(AssistantFavoritesActions.addFavoriteSuccess, (state, { favorite }) =>
      entityAdapter.upsertOne(favorite, { ...state, error: null }),
    ),
    on(
      AssistantFavoritesActions.addFavoriteFailure,
      (state, { assistantId, error }) =>
        entityAdapter.removeOne(assistantId, { ...state, error }),
    ),

    on(
      AssistantFavoritesActions.removeFavoriteSuccess,
      (state, { assistantId }) =>
        entityAdapter.removeOne(assistantId, { ...state, error: null }),
    ),
    on(AssistantFavoritesActions.removeFavoriteFailure, (state, { error }) => ({
      ...state,
      error,
    })),

    on(
      AssistantFavoritesActions.updateSortOrder,
      (state, { assistantId, sortOrder }) =>
        entityAdapter.updateOne(
          { id: assistantId, changes: { sortOrder } },
          { ...state, error: null },
        ),
    ),
    on(
      AssistantFavoritesActions.updateSortOrderSuccess,
      (state, { assistantId, sortOrder }) =>
        entityAdapter.updateOne(
          { id: assistantId, changes: { sortOrder } },
          { ...state, error: null },
        ),
    ),
    on(
      AssistantFavoritesActions.updateSortOrderFailure,
      (
        state,
        { assistantId, attemptedSortOrder, previousSortOrder, error },
      ) => {
        const current = state.entities[assistantId];
        if (current?.sortOrder !== attemptedSortOrder) {
          return { ...state, error };
        }

        return entityAdapter.updateOne(
          { id: assistantId, changes: { sortOrder: previousSortOrder } },
          { ...state, error },
        );
      },
    ),
  ),
  extraSelectors: ({ selectAssistantFavoritesState }) => {
    const entitySelectors = entityAdapter.getSelectors(
      selectAssistantFavoritesState,
    );

    return {
      ...entitySelectors,

      /** All favorites sorted by sortOrder through the entity adapter sortComparer. */
      selectFavoritesSorted: entitySelectors.selectAll,

      /** Returns a predicate function: `selectIsFavorite(assistantId)`. */
      selectIsFavorite: (assistantId: string) =>
        createSelector(
          entitySelectors.selectEntities,
          (entities) => assistantId in entities,
        ),
    };
  },
});

function getFirstSortOrder(state: AssistantFavoritesState): string | null {
  if (state.ids.length === 0) {
    return null;
  }

  const firstId = state.ids[0];

  return state.entities[firstId]?.sortOrder ?? null;
}
